import { FC, useCallback, useState } from "react";
import { Button, Badge, Alert } from "react-bootstrap";
import Loader from "src/components/common/loader/loader";
import useOrganizationUsers from "../hooks/useOrganizationUsers";
import AddUserModal from "./AddUserModal";
import ManageRolesModal from "./ManageRolesModal";
import ModalDelete from "../../../../components/common/ModalDelete";
import ImpersonationStartModal from "../../../../components/ImpersonationStartModal";
import ReactivateUserModal from "./ReactivateUserModal";
import AggregateVersionsDrawer from "@/shared/components/versions/AggregateVersionsDrawer";
import HistoryButton from "@/shared/components/versions/HistoryButton";
import type { AggregateVersion } from "@models/aggregateVersion.model";
import { PermissionService } from "@services/permissions/permissionService";
import type { OrgUser } from "@services/permissions/commands/cmdListOrgUsers";
import { appStore, startViewingAs } from "@store/appStore";
import { PermissionLevels } from "@models/permission.model";

interface Props {
  organizationId: number;
}

const OrganizationUsersTab: FC<Props> = ({ organizationId }) => {
  const { users, roles, isLoading, hasLoaded, isSaving, error, activateUser, addUser, createUser, removeUser, repairOrg, updateRoles } =
    useOrganizationUsers(organizationId);

  const [showAddModal, setShowAddModal] = useState(false);
  const [rolesTarget, setRolesTarget] = useState<OrgUser | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<{ profileId: number; label: string } | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [viewAsTarget, setViewAsTarget] = useState<OrgUser | null>(null);
  const [activateTarget, setActivateTarget] = useState<OrgUser | null>(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [versions, setVersions] = useState<AggregateVersion[] | null>(null);
  const [historyError, setHistoryError] = useState(false);

  // Users EDIT, the level that already governs the pencil in the next column.
  // Restoring an account that keeps its roles grants no more than setting those
  // roles does, and the server asks for the same level.
  const mayEditUsers =
    appStore.loggedInUser?.permissions?.hasAccess?.("Users", PermissionLevels.UPDATE) ?? false;

  const startImpersonation = async (
    reason: string,
    proof: { password?: string; googleIdToken?: string },
  ) => {
    if (!viewAsTarget) return;
    await startViewingAs(
      viewAsTarget.user_id,
      `${viewAsTarget.first_name} ${viewAsTarget.last_name}`,
      reason,
      proof,
    );
  };

  const handleAdd = async (profileId: number) => {
    await addUser(profileId);
    setShowAddModal(false);
  };

  const handleCreate = async (payload: {
    email: string;
    firstName: string;
    lastName: string;
    roleIds: number[];
    sendInvite: boolean;
  }) => {
    const ok = await createUser(payload);
    if (ok) setShowAddModal(false);
    return ok;
  };

  const handleSaveRoles = async (roleIds: number[]) => {
    if (!rolesTarget) return;
    await updateRoles(rolesTarget.profile_id, roleIds);
    setRolesTarget(null);
  };

  // Always refetch on open. Every write on this tab goes through the hook rather than through
  // this component, so cached rows would show the history as it stood before the change the
  // user just made.
  const openHistory = useCallback(async () => {
    setHistoryOpen(true);
    setVersions(null);
    setHistoryError(false);
    try {
      setVersions(await new PermissionService().listOrgUserVersions(organizationId));
    } catch {
      setVersions([]);
      setHistoryError(true);
    }
  }, [organizationId]);

  const handleActivate = async () => {
    if (!activateTarget) return;
    await activateUser(activateTarget.profile_id);
    setActivateTarget(null);
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setIsDeleting(true);
    try {
      await removeUser(deleteTarget.profileId);
    } finally {
      setIsDeleting(false);
      setDeleteTarget(null);
    }
  };

  if (isLoading && !hasLoaded) {
    return <Loader />;
  }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h5 className="mb-1">Users</h5>
          <p className="text-muted small mb-0">
            Manage users and their roles for this organization
          </p>
        </div>
        {/* No count badge: the list is fetched on open, and a badge reading 0 until you
          click is worse than none. */}
        <div className="d-flex align-items-center gap-2">
          <HistoryButton onClick={openHistory} title="Who changed this organization's users" />
          <Button
            variant="primary"
            size="sm"
            onClick={() => setShowAddModal(true)}
            className="d-flex align-items-center gap-2"
          >
            <i className="bi bi-plus-circle"></i>
            Add User
          </Button>
        </div>
      </div>

      {error && <Alert variant="danger" className="mb-3">{error}</Alert>}

      {users.length === 0 ? (
        <Alert variant="info">
          <div className="d-flex align-items-start gap-3">
            <i className="bi bi-info-circle fs-4"></i>
            <div className="flex-grow-1">
              <strong>No users yet</strong>
              <p className="mb-2 mt-1">
                Add users to this organization to manage their access and roles.
              </p>
              <Button variant="primary" size="sm" onClick={() => setShowAddModal(true)}>
                <i className="bi bi-plus-circle me-2"></i>
                Add User
              </Button>
            </div>
          </div>
        </Alert>
      ) : (
        <div className="border rounded">
          <table className="table table-hover mb-0">
            <thead className="table-light">
              <tr>
                <th className="small fw-semibold">Name</th>
                <th className="small fw-semibold">Email</th>
                <th className="small fw-semibold">Roles</th>
                <th className="small fw-semibold" style={{ width: 100 }}></th>
              </tr>
            </thead>
            <tbody>
              {users.map((user) => (
                <tr
                  key={user.profile_id}
                  // Recede the whole row rather than hide it — an admin still
                  // needs to find a leaver — but stop a disabled account reading
                  // as a live one. Deactivation keeps roles attached, so without
                  // this a departed colleague shows a Super Admin badge.
                  className={user.is_active ? undefined : "opacity-50"}
                >
                  <td className="small align-middle">
                    <span className={user.is_active ? undefined : "text-decoration-line-through"}>
                      {user.first_name} {user.last_name}
                    </span>
                    {!user.is_active && (
                      <Badge bg="secondary-transparent" text="secondary" className="ms-2 fw-normal">
                        Deactivated
                      </Badge>
                    )}
                    {/* Listed here but absent from every contact picker, because
                      the employee record places them in no org this profile
                      names. Invisible until we said so — the row otherwise reads
                      as correctly configured. */}
                    {user.needs_org_repair && (
                      <Badge
                        bg="warning-transparent"
                        text="warning"
                        className="ms-2 fw-normal"
                        title="This person's employee record does not place them in this organization, so they will not appear in engagement contact lists. Use Fix to repair it."
                      >
                        <i className="bi bi-exclamation-triangle me-1" />
                        Not assigned
                      </Badge>
                    )}
                  </td>
                  <td className="small align-middle text-muted">{user.email}</td>
                  <td className="align-middle">
                    {/* Every role, wrapped. This cell used to cap the list at three
                      chips inside a 250px box with overflow hidden, and the two
                      combined to hide the fourth role AND the "+1" that was meant
                      to announce it -- both sat past the clip. Granting a role
                      changed nothing on screen, which is what "the rollup does not
                      reflect new roles" turned out to be (MPN-1606). Nine roles
                      exist in total and their names are short, so there is nothing
                      here worth truncating. */}
                    <div
                      className="d-flex align-items-center flex-wrap gap-1"
                      style={{ cursor: "pointer" }}
                      onClick={() => setRolesTarget({ ...user, roles: [...user.roles] })}
                      title="Click to manage roles"
                    >
                      {user.roles.length === 0 ? (
                        <span className="text-muted small fst-italic">No roles</span>
                      ) : (
                        user.roles.map((r) => (
                          <Badge key={r.id} bg="light" text="dark" className="border px-2">{r.name}</Badge>
                        ))
                      )}
                      <i className="bi bi-pencil text-muted ms-1" style={{ fontSize: "0.7rem" }}></i>
                    </div>
                  </td>
                  <td className="align-middle text-end">
                    <div className="d-flex align-items-center justify-content-end gap-1">
                      {/* View As User, not Platform Admin: the matrix narrowed impersonation to
                        Super Admin, and Power User still holds Platform Admin. */}
                      {/* Hidden when deactivated: the server refuses a session against
                        an inactive target, since token-login would reject the token it
                        mints. Better to not offer it than to 403. */}
                      {user.is_active &&
                        (appStore.loggedInUser?.permissions?.hasAccess?.("View As User") ?? false) && (
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          onClick={() => setViewAsTarget({...user, roles: [...user.roles.map(r => ({...r}))]})}
                          title="View platform as this user"
                          style={{ width: 32, height: 32, padding: 0, display: "flex", alignItems: "center", justifyContent: "center" }}
                        >
                          <i className="bi bi-eye"></i>
                        </Button>
                      )}
                      {/* Where the eye sits on a live row. The flag is the only
                        thing the login path reads, so this is what brings a
                        leaver back; the trash beside it removes them from the
                        organization instead and leaves the flag alone. */}
                      {!user.is_active && mayEditUsers && (
                        <Button
                          variant="outline-success"
                          size="sm"
                          onClick={() => setActivateTarget({ ...user, roles: [...user.roles.map((r) => ({ ...r }))] })}
                          disabled={isSaving}
                          title="Reactivate this account"
                          style={{ width: 32, height: 32, padding: 0, display: "flex", alignItems: "center", justifyContent: "center" }}
                        >
                          <i className="bi bi-arrow-counterclockwise"></i>
                        </Button>
                      )}
                      {user.needs_org_repair && (
                        <Button
                          variant="outline-warning"
                          size="sm"
                          onClick={() => repairOrg(user.profile_id)}
                          disabled={isSaving}
                          title="Assign this person's employee record to this organization"
                          style={{ height: 32, padding: "0 0.6rem", display: "flex", alignItems: "center" }}
                        >
                          <i className="bi bi-wrench-adjustable me-1" />
                          Fix
                        </Button>
                      )}
                      <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={() =>
                          setDeleteTarget({
                            profileId: user.profile_id,
                            label: `${user.first_name} ${user.last_name}`,
                          })
                        }
                        title="Remove from organization"
                        style={{ width: 32, height: 32, padding: 0, display: "flex", alignItems: "center", justifyContent: "center" }}
                      >
                        <i className="bi bi-trash"></i>
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <ManageRolesModal
        show={rolesTarget !== null}
        onClose={() => setRolesTarget(null)}
        onSave={handleSaveRoles}
        userName={rolesTarget ? `${rolesTarget.first_name} ${rolesTarget.last_name}` : ""}
        currentRoleIds={rolesTarget?.roles.map((r) => r.id) || []}
        allRoles={[...roles]}
        isSaving={isSaving}
      />

      <AddUserModal
        show={showAddModal}
        onClose={() => setShowAddModal(false)}
        onAdd={handleAdd}
        onCreate={handleCreate}
        existingUserIds={users.map((u) => u.user_id)}
        isSaving={isSaving}
        allRoles={[...roles]}
      />

      <ImpersonationStartModal
        show={viewAsTarget !== null}
        targetName={viewAsTarget ? `${viewAsTarget.first_name} ${viewAsTarget.last_name}` : ""}
        onConfirm={startImpersonation}
        onClose={() => setViewAsTarget(null)}
      />

      <AggregateVersionsDrawer
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
        versions={versions ?? []}
        title="Users history"
        subtitle="Who changed this organization's users"
        emptyMessage={
          historyError
            ? "Could not load the user history."
            : versions === null
              ? "Loading…"
              : "No history yet. Adding, removing or changing a user creates the first version."
        }
      />

      <ReactivateUserModal
        show={activateTarget !== null}
        onClose={() => setActivateTarget(null)}
        onConfirm={handleActivate}
        user={activateTarget}
        isSaving={isSaving}
      />

      <ModalDelete
        title="User"
        objectLabel={deleteTarget?.label || ""}
        deleteFn={handleDelete}
        visible={deleteTarget !== null}
        toggleVisibility={(visible: boolean) => {
          if (!visible) setDeleteTarget(null);
        }}
        isLoading={isDeleting}
      />
    </div>
  );
};

export default OrganizationUsersTab;
